// ✅ generateMaterielReparationsPDF.js
// PDF Historique réparations matériel
// ✅ Compatible ancien format + nouveau format (context)
// ✅ Synthèse AVANT tableau
// ✅ Chantier affiché par NOM si possible
// ✅ Totaux = PRIX * QTE (+ TVA)
// ✅ Fix jsPDF "2/ 400" (espaces insécables)

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const safe = (v) => (v === null || v === undefined ? "" : String(v));

const toNumber = (v, def = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : def;
};

// ✅ jsPDF n'aime pas U+202F / U+00A0 -> il peut afficher "/"
const normalizeSpaces = (s) =>
  String(s ?? "")
    .replace(/\u202F/g, " ")
    .replace(/\u00A0/g, " ");

const fmtMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0,00";
  const formatted = new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(n);
  return normalizeSpaces(formatted);
};

// ✅ TAXE : "20" => 0.2 | 0.2 => 0.2 | "20%" => 0.2
const parseTaxRate = (raw) => {
  if (raw === null || raw === undefined || raw === "") return 0;

  const s = String(raw).replace(",", ".").replace("%", "").trim();
  const n = Number(s);
  if (!Number.isFinite(n)) return 0;
  return n > 1 ? n / 100 : n;
};

const fmtPercent = (rate) => {
  const r = Number(rate) || 0;
  return `${Math.round(r * 100)}%`;
};

const getChantierLabel = (r, chantiersMap = {}) => {
  // priorité au NOM
  const name =
    r?.chantier_name ||
    r?.CHANTIER_NAME ||
    r?.chantier ||
    r?.chantier_actuel ||
    "";

  if (name) return normalizeSpaces(name);

  // sinon via map id->name
  const id =
    r?.chantier_id ??
    r?.id_chantier ??
    r?.IDCHANTIER ??
    r?.ID_CHANTIER ??
    "";

  if (id && chantiersMap[id]) return normalizeSpaces(chantiersMap[id]);
  if (id) return `#${normalizeSpaces(id)}`;
  return "";
};

export const generateMaterielReparationsPDF = (payload) => {
  // ✅ Compat : ancien format vs nouveau format
  // ancien: { generatedAt, userName, materiel, chantierName, rows, chantiersMap }
  // nouveau: { context, rows, chantiersMap }

  const context = payload?.context || {
    generatedAt: payload?.generatedAt,
    userName: payload?.userName,
    materiel: payload?.materiel,
    chantierName: payload?.chantierName
  };

  const rows = Array.isArray(payload?.rows) ? payload.rows : [];
  const chantiersMap = payload?.chantiersMap || {};

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4"
  });

  const generatedAt = context?.generatedAt ? new Date(context.generatedAt) : new Date();
  const generatedAtStr = normalizeSpaces(generatedAt.toLocaleString("fr-FR"));

  const userName = normalizeSpaces(safe(context?.userName));
  const materiel = context?.materiel || {};
  const chantierActuel = normalizeSpaces(safe(materiel?.chantier_actuel || context?.chantierName || ""));

  // =========================
  // 🧾 En-tête
  // =========================
  doc.setFontSize(16);
  doc.text("Fiche Réparations – Matériel (Historique)", 14, 15);

  doc.setFontSize(10);
  doc.text(`Généré le : ${generatedAtStr}`, 14, 22);
  doc.text(`Utilisateur : ${userName}`, 14, 27);

  // =========================
  // 🚜 Bloc Engin
  // =========================
  doc.setFontSize(12);
  doc.text("Données de l’engin", 14, 35);

  doc.setFontSize(10);
  const leftX = 14;
  const midX = 145;
  let y = 42;

  doc.text(`ID Engin : ${normalizeSpaces(safe(materiel?.id))}`, leftX, y);
  doc.text(`Matricule : ${normalizeSpaces(safe(materiel?.matricule))}`, leftX, y + 6);
  doc.text(`Type : ${normalizeSpaces(safe(materiel?.type))}`, leftX, y + 12);
  doc.text(`Société : ${normalizeSpaces(safe(materiel?.societe))}`, leftX, y + 18);

  doc.text(`Chantier actuel : ${chantierActuel}`, midX, y);
  doc.text(`État : ${normalizeSpaces(safe(materiel?.etat))}`, midX, y + 6);

  y = y + 26;

  // =========================
  // 🔎 Calcul lignes + totaux
  // =========================
  const computed = rows.map((r) => {
    const qte = toNumber(r.QTE ?? r.qte ?? r.QTE_INIT, 0);
    const prix = toNumber(r.PRIX ?? r.prix, 0);
    const taxeRate = parseTaxRate(r.TAXE ?? r.taxe);

    const htUnit = prix;
    const ttcUnit = prix * (1 + taxeRate);

    const htTotal = htUnit * qte;
    const ttcTotal = ttcUnit * qte;

    return {
      ...r,
      __qte: qte,
      __taxeRate: taxeRate,
      __htUnit: htUnit,
      __ttcUnit: ttcUnit,
      __htTotal: htTotal,
      __ttcTotal: ttcTotal
    };
  });

  const totalHT = computed.reduce((acc, r) => acc + (Number(r.__htTotal) || 0), 0);
  const totalTTC = computed.reduce((acc, r) => acc + (Number(r.__ttcTotal) || 0), 0);
  const totalTVA = totalTTC - totalHT;

  // =========================
  // 📊 Synthèse (AVANT tableau)
  // =========================
  const synthY = y + 10;

  doc.setFontSize(12);
  doc.text("Synthèse", 14, synthY);

  doc.setDrawColor(109, 78, 54);
  doc.setLineWidth(0.3);
  doc.roundedRect(14, synthY + 0, 270, 22, 2, 2);

  doc.setFontSize(11);
  doc.text(`Nombre de lignes : ${computed.length}`, 18, synthY + 10);

  doc.setFontSize(12);
  doc.text(`Total HT : ${fmtMoney(totalHT)} DH`, 18, synthY + 17);
  doc.text(`Total TVA : ${fmtMoney(totalTVA)} DH`, 115, synthY + 17);
  doc.text(`Total TTC : ${fmtMoney(totalTTC)} DH`, 200, synthY + 17);

  // =========================
  // 📄 Tableau
  // =========================
  const tableStartY = synthY + 32;

  doc.setFontSize(12);
  doc.text("Historique des réparations", 14, tableStartY - 6);

  autoTable(doc, {
    startY: tableStartY,
    head: [[
      "Date BDC",
      "IDBDC",
      "Chantier",
      "Fournisseur",
      "Code",
      "Désignation",
      "Qté",
      "Unité",
      "HT (unit)",
      "TVA %",
      "TTC (unit)",
      "Total HT",
      "Total TTC"
    ]],
    body: computed.length
      ? computed.map((r) => ([
          normalizeSpaces(safe(r.DATE_BDC ?? r.date_bdc ?? r.created_at ?? r.createdAt ?? "")),
          normalizeSpaces(safe(r.IDBDC ?? r.idbdc ?? "")),
          getChantierLabel(r, chantiersMap),
          normalizeSpaces(safe(r.FOURNISSEUR_LIBELLE ?? r.fournisseur_libelle ?? "")),
          normalizeSpaces(safe(r.CODE_PRODUIT ?? r.code_produit ?? "")),
          normalizeSpaces(safe(r.DESIGNATION ?? r.designation ?? "")),
          normalizeSpaces(safe(r.__qte)),
          normalizeSpaces(safe(r.UNITE ?? r.unite ?? "")),
          fmtMoney(r.__htUnit),
          fmtPercent(r.__taxeRate),
          fmtMoney(r.__ttcUnit),
          fmtMoney(r.__htTotal),
          fmtMoney(r.__ttcTotal)
        ]))
      : [["—", "—", "—", "—", "—", "—", "—", "—", "—", "—", "—", "—", "—"]],
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [109, 78, 54] },
    columnStyles: {
      5: { cellWidth: 52 }, // Désignation
      3: { cellWidth: 28 }, // Fournisseur
      2: { cellWidth: 22 }  // Chantier
    }
  });

  const endY = (doc.lastAutoTable?.finalY || tableStartY + 20) + 6;
  doc.setFontSize(9);
  doc.setTextColor(80);
  doc.text(
    `Rappel Totaux — HT: ${fmtMoney(totalHT)} DH | TVA: ${fmtMoney(totalTVA)} DH | TTC: ${fmtMoney(totalTTC)} DH`,
    14,
    endY
  );
  doc.setTextColor(0);

  // =========================
  // 💾 Save
  // =========================
  const matricule = normalizeSpaces(safe(materiel?.matricule)) || "SANS_MATRICULE";
  const fileSafeDate = generatedAtStr.replace(/[^\dA-Za-z-_]/g, "_");
  doc.save(`historique_reparations_${matricule}_${fileSafeDate}.pdf`);
};
